import { useMemo } from 'react';
import { useFormikContext } from 'formik';
import { SelectionAdapter } from 'components/SelectPropertiesDrawerV2';
import { useAgOperationUtility } from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import {
  FormValues,
  CropZone
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';

/**
 * Wires SelectPropertiesDrawerV2 to the Work Order / Application Formik form.
 *
 * SelectPropertiesDrawerV2 is decoupled from any state management via the
 * SelectionAdapter interface — it knows nothing about Formik. This hook is the
 * bridge: it reads the current crop zone selection from Formik and writes the
 * confirmed selection back when the user saves the drawer.
 *
 * To add drawer support in another context (e.g. Yield), create a sibling hook
 * (e.g. useYieldSelectionAdapter) that reads/writes whatever state that screen
 * uses, and pass its return value as the `selectionAdapter` prop on the drawer.
 *
 * Must be called inside a Formik context (i.e. within a <Formik> provider).
 */
const useOperationSelectionAdapter = (): SelectionAdapter => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const { utilityState } = useAgOperationUtility();

  return useMemo(
    () => ({
      initialCropZones: values.cropZones || [],
      existingCropZones: values.cropZones || [],
      // PropertiesStep (and the drawer) unmounts/remounts on every step
      // change, so this comes from AgOperationUtilityProvider — set once
      // from the record as originally loaded, above the step wizard — not
      // from a value scoped to the drawer's own mount lifetime.
      alwaysVisibleCropZoneIds: utilityState.originalCropZoneIds ?? [],
      onConfirm: ({
        cropZones,
        totalArea
      }: {
        cropZoneIds: string[];
        totalArea: number;
        cropZones: CropZone[];
        sourceType: string;
      }) => {
        setFieldValue('cropZones', cropZones);
        setFieldValue('totalArea', totalArea);
        setFieldValue('cropZoneSourceType', 'CROP_ZONE');
      }
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.cropZones, utilityState.originalCropZoneIds]
  );
};

export default useOperationSelectionAdapter;
